import * as rrweb from 'rrweb'
import { startRecordSession, uploadRecordChunk } from '@/layouts/api'
import { v4 as uuid } from 'uuid'
import { listenerHandler } from '@rrweb/types'

interface RRWebSession {
  session_id: string;
  page_url: string;
  browser: string;
  os: string;
  screen_width: number;
  screen_height: number;
}

interface RRWebChunk {
  session_id: string;
  events: any[];
  timestamp: number;
}

const CHUNK_INTERVAL = 10000 // 10 seconds

let session: RRWebSession | null = null
let events: any[] = []
let lastUploadTimestamp = 0
let stopFn: listenerHandler | undefined = undefined
let intervalId: number | null = null

function isHighPerformanceDevice() {

  return false // 以后再打开

  // 获取设备内存（以 GB 为单位）
  const deviceMemory = navigator?.deviceMemory || 4 // 如果无法获取，假设为 4GB

  // 获取 CPU 线程数
  const hardwareConcurrency = navigator.hardwareConcurrency || 4 // 如果无法获取，假设为 4 核

  // 自定义基准测试：测量一个复杂计算任务的执行时间
  const start = performance.now()
  for (let i = 0; i < 1e6; i += 100) { // 减少循环次数
    Math.sqrt(i)
  }
  const end = performance.now()
  const executionTime = end - start

  // 性能阈值（可以根据实际情况调整）
  const memoryThreshold = 4 // 4GB 内存
  const cpuThreshold = 4 // 4 核 CPU
  const executionTimeThreshold = 50 // 50ms 计算时间

  // 判断设备性能
  return deviceMemory >= memoryThreshold &&
    hardwareConcurrency >= cpuThreshold &&
    executionTime <= executionTimeThreshold
}

const flagPromise = new Promise<boolean>((resolve) => {
  setTimeout(() => {
    resolve(isHighPerformanceDevice())
  }, 0)
})

const createSession = (): RRWebSession => ({
  session_id: uuid(),
  page_url: window.location.href,
  browser: navigator.userAgent,
  os: navigator.platform,
  screen_width: window.screen.width,
  screen_height: window.screen.height,
})

const uploadChunk = async (forceUpload: boolean = false) => {
  const now = Date.now()
  if (events.length > 0 && (forceUpload || now - lastUploadTimestamp >= CHUNK_INTERVAL)) {
    const chunk: RRWebChunk = {
      session_id: session!.session_id,
      events: events,
      timestamp: now,
    }

    try {
      await uploadRecordChunk(chunk)
      events = []
      lastUploadTimestamp = now
    } catch (error) {
      console.error('Failed to upload rrweb chunk:', error)
    }
  }
}

const handleVisibilityChange = () => {
  if (document.visibilityState === 'hidden') {
    uploadChunk(true)
  }
}

export const startRecording = async () => {
  const flag = await flagPromise
  if (!flag) {
    return
  }
  if (stopFn) {
    console.warn('Recording is already in progress. Call stopRecording() first.')
    return null
  }

  session = createSession()

  try {
    await startRecordSession({
      type: 'session_start',
      data: session,
    })
  } catch (error) {
    console.error('Failed to start recording session:', error)
    return null
  }
  stopFn = rrweb.record({
    emit(event) {
      events.push(event)
    },
    checkoutEveryNms: CHUNK_INTERVAL,
    checkoutEveryNth: 200,
    sampling: {
      mousemove: false,
      mouseInteraction: false,
      scroll: 300, // 每 300ms 最多触发一次
      media: 800,
      input: 'last', // 连续输入时，只录制最终值
    },
    recordCanvas: true, // 记录 canvas 事件
    recordCrossOriginIframes: false, // 不记录跨域 iframe 事件
  })

  intervalId = window.setInterval(() => uploadChunk(), CHUNK_INTERVAL)

  document.addEventListener('visibilitychange', handleVisibilityChange)

  return session.session_id
}

export const stopRecording = async () => {
  const flag = await flagPromise
  if (!flag) {
    return
  }
  if (!stopFn) {
    console.warn('No recording in progress.')
    return
  }

  stopFn()
  stopFn = undefined

  if (intervalId !== null) {
    clearInterval(intervalId)
    intervalId = null
  }

  document.removeEventListener('visibilitychange', handleVisibilityChange)

  await uploadChunk(true) // Final upload

  session = null
  events = []
  lastUploadTimestamp = 0
}

export const getSessionId = () => session?.session_id || null
